/**
 * The single time scope for the Statistics page.
 *
 * One control scopes every section below it, so the numbers always agree. The
 * complication is that the endpoints have different reach, verified against
 * ReceiverStatsService:
 *
 *   getReceiverDailyBreakdown   MAX_DAILY_DAYS = 90
 *   getReceiverHourlyBreakdown  ONE date only
 *   getReceiverReportHeatmap    MAX_HEATMAP_DAYS = 14
 *   getReceiverHeatmap          ONE date only (coverage polar)
 *
 * So a resolved range carries a field per consumer rather than one number, and
 * anything that cannot span the selection exposes a `note` the card renders —
 * we never silently truncate and let the reader assume full coverage.
 */

export const RANGE_PRESETS = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '24h', label: '24h', days: 2 },
  { id: '7d', label: '7d', days: 7 },
  { id: '14d', label: '14d', days: 14 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
];

export const DEFAULT_RANGE = { preset: '7d', date: null };

const MAX_HEATMAP_DAYS = 14;   // ReceiverStatsService.MAX_HEATMAP_DAYS
const MAX_DAILY_DAYS = 90;     // ReceiverStatsService.MAX_DAILY_DAYS

/** yyyy-MM-dd for a Date, in UTC — the buckets the backend counts in. */
export function utcDay(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
    .toISOString().slice(0, 10);
}

/**
 * Down-sample step for getReceiverStats.
 *
 * Left to itself the backend's pickStep returns 30 minutes for anything over a
 * day, which at 90 days is 4,320 buckets per metric across 11 metrics — a
 * payload nobody can read and the browser has to parse anyway. Target a fixed
 * budget of plottable points instead; a chart card is only ~500px wide, so more
 * than that is sub-pixel detail by definition.
 */
export function statsStep(rangeSeconds, targetPoints = 480) {
  const raw = Math.ceil(rangeSeconds / targetPoints);
  // Never finer than the 60s STATS heartbeat — asking for 10s buckets over a
  // 60s cadence just returns mostly-empty buckets.
  return Math.max(60, Math.round(raw / 60) * 60);
}

/**
 * Turn {preset, date} into everything the sections need.
 *
 * `now` is injectable so this is testable without freezing the clock.
 */
export function resolveRange({ preset, date } = DEFAULT_RANGE, now = new Date()) {
  // An explicit date pins the whole page to that single day.
  if (date) {
    const to = new Date(`${date}T23:59:59.999Z`);
    const from = new Date(`${date}T00:00:00.000Z`);
    return {
      id: 'date',
      label: date,
      days: 1,
      day: date,
      isSingleDay: true,
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      stepSeconds: statsStep(24 * 3600),
      heatmapDays: 7,
      heatmapNote: null,
    };
  }

  const p = RANGE_PRESETS.find(x => x.id === preset) || RANGE_PRESETS[2];
  const days = Math.min(p.days, MAX_DAILY_DAYS);

  // 'today' and '24h' differ in what they ask the daily chart for: today is one
  // bar, 24h spans the midnight boundary and so needs two.
  const rangeSeconds = p.id === 'today'
    ? secondsSinceUtcMidnight(now)
    : p.id === '24h' ? 24 * 3600 : days * 24 * 3600;

  const to = now;
  const from = new Date(now.getTime() - rangeSeconds * 1000);

  const heatmapDays = Math.min(days, MAX_HEATMAP_DAYS);
  const heatmapNote = days > MAX_HEATMAP_DAYS
    ? `last ${MAX_HEATMAP_DAYS} days (source limit)`
    : null;

  return {
    id: p.id,
    label: p.label,
    days,
    // Single-day charts (hourly, coverage, fleet) use the newest day in range.
    day: utcDay(now),
    isSingleDay: p.id === 'today',
    fromIso: from.toISOString(),
    toIso: to.toISOString(),
    stepSeconds: statsStep(rangeSeconds),
    heatmapDays,
    heatmapNote,
  };
}

function secondsSinceUtcMidnight(now) {
  const midnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  // Guard the first seconds after midnight, where a zero-width range would
  // make every chart empty.
  return Math.max(3600, Math.floor((now.getTime() - midnight) / 1000));
}

/** Note for a single-day chart shown inside a multi-day range. */
export function singleDayNote(range) {
  return range.days > 1 ? `${range.day} only` : null;
}

/** Continuous yyyy-MM-dd list ending today (UTC), ascending. */
export function utcDateRange(daysBack, now = new Date()) {
  const out = [];
  for (let i = daysBack - 1; i >= 0; i--) {
    out.push(new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i)).toISOString().slice(0, 10));
  }
  return out;
}

/**
 * Daily buckets -> chart rows, filling gaps with 0.
 *
 * The backend returns only days that have data, so without the fill a quiet
 * Sunday silently vanishes and the bars either side sit next to each other as
 * though they were consecutive.
 */
export function dailyRows(buckets, keys, days, now = new Date()) {
  const byDate = new Map((buckets || []).map(b => [b.date, b.metrics || {}]));
  return utcDateRange(days, now).map(date => {
    const m = byDate.get(date) || {};
    return {
      label: date.slice(5).replace('-', '/'), // MM/DD
      values: keys.map(k => m[k] || 0),
    };
  });
}

/** Hourly buckets -> 24 chart rows, filling gaps with 0. */
export function hourlyRows(buckets, keys) {
  const byHour = new Map((buckets || []).map(b => [b.hour, b.metrics || {}]));
  return Array.from({ length: 24 }, (_, h) => {
    const m = byHour.get(h) || {};
    return {
      label: String(h).padStart(2, '0'),
      values: keys.map(k => m[k] || 0),
    };
  });
}
